import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import ApplyJob from '../components/Company/apply_job';
import JoinOurTeam from '../components/Company/JoinOurTeam';
import Footer from '../components/layout/footer';

import { Search, Briefcase, Calendar, Clock, ArrowRight, MapPin, FileText, ListChecks, AlertCircle, ChevronDown, X } from 'lucide-react';

const DESKTOP_QUERY = '(min-width: 1024px)';

const Careers = () => {
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const applyPanelRef = useRef(null);

  // Mobile uses tap-to-expand cards plus a full-screen apply sheet;
  // desktop keeps the side-by-side list and form.
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia(DESKTOP_QUERY).matches);
  const [expandedId, setExpandedId] = useState(null);
  const [sheetJob, setSheetJob] = useState(null);

  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_QUERY);
    const onChange = (e) => {
      setIsDesktop(e.matches);
      if (e.matches) setSheetJob(null); // sheet is mobile-only
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Stop the page behind the sheet from scrolling, and close it on Escape
  useEffect(() => {
    if (!sheetJob) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onEscape = (e) => { if (e.key === 'Escape') setSheetJob(null); };
    document.addEventListener('keydown', onEscape);

    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onEscape);
    };
  }, [sheetJob]);

  const handleCardClick = (job) => {
    if (isDesktop) {
      setSelectedJob(job);
    } else {
      // Tapping the open card closes it again
      setExpandedId(prev => (prev === job.id ? null : job.id));
    }
  };

  useEffect(() => {
    const handleContextMenu = (e) => e.preventDefault();

    const handleKeyDown = (e) => {
      if (
        e.keyCode === 123 || // F12
        (e.ctrlKey && e.shiftKey && (e.keyCode === 73 || e.keyCode === 74)) || // Ctrl+Shift+I / J
        (e.ctrlKey && e.keyCode === 85) // Ctrl+U
      ) {
        e.preventDefault();
      }
    };

    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("keydown", handleKeyDown);

    axios.get('/api/jobs/')
      .then(res => {
        const today = new Date();
        const todayReset = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();

        const sortedData = res.data.sort((a, b) => {
          const deadlineA = new Date(a.application_deadline).getTime();
          const deadlineB = new Date(b.application_deadline).getTime();
          const postedA = new Date(a.posted_on).getTime();
          const postedB = new Date(b.posted_on).getTime();

          const isExpiredA = deadlineA < todayReset;
          const isExpiredB = deadlineB < todayReset;

          // 1. Move expired jobs to the bottom
          if (isExpiredA !== isExpiredB) {
            return isExpiredA ? 1 : -1; 
          }

          // 2. If both are active (or both expired), show newest first
          return postedB - postedA;
        });

        setJobs(sortedData);
        setFilteredJobs(sortedData);
        setLoading(false);
      })
      .catch(err => console.error("API Error:", err));

    return () => {
        document.removeEventListener("contextmenu", handleContextMenu);
        document.removeEventListener("keydown", handleKeyDown);
      };

  }, []);
  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    setFilteredJobs(
      jobs.filter(job =>
        job.title.toLowerCase().includes(value) ||
        job.location.toLowerCase().includes(value)
      )
    );
  };

  const today = new Date();
  // Reset time to midnight to ensure comparison is based strictly on the date
  const todayReset = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  // A job is expired once its deadline date has passed (deadline day itself still counts as open)
  const isJobExpired = (job) => {
    const deadline = new Date(job.application_deadline).getTime();
    return isNaN(deadline) ? false : deadline < todayReset.getTime();
  };

  return (
    <div className="min-h-screen pt-16 pb-20 px-4 sm:px-6 text-[#E6FCEF] selection:bg-green-500/30 overflow-x-hidden">

      <section id="JoinOurTeam" className="scroll-mt-14">
        <JoinOurTeam />

      </section>
      <div className="max-w-7xl py-16 md:py-32 mx-auto">

        {/* HEADER */}
        <header className="mb-10 md:mb-14">

          <p className="text-orange-500 text-lg sm:text-2xl md:text-3xl text-center max-w-7xl mt-4">
            Join our mission to defend the digital frontier. Explore open roles and apply today.
          </p>
        </header>

        {/* SEARCH */}
        <div id="openjobs" className="relative mb-10 md:mb-12 group">
          <Search className="absolute left-4 sm:left-6 top-1/2 -translate-y-1/2 text-green-300/60 w-5 h-5 sm:w-[22px] sm:h-[22px]" />
          <input
            type="text"
            placeholder="Search for roles or locations..."
            value={searchTerm}
            onChange={handleSearch}
            className="w-full bg-[#04150D]/70 backdrop-blur-xl
              border border-green-500/20 rounded-2xl
              py-4 sm:py-6 pl-12 sm:pl-16 pr-4 md:pr-40 text-sm sm:text-base md:text-lg text-white
              placeholder:text-green-300/40
              focus:border-green-400 focus:ring-4 focus:ring-green-500/10
              transition-all"
          />
          <button className="hidden md:block absolute right-3 top-1/2 -translate-y-1/2
            bg-green-500 hover:bg-green-400
            text-[#02130C] font-black uppercase tracking-widest
            py-3.5 px-10 rounded-xl
            shadow-[0_0_30px_rgba(34,197,94,0.35)]">
            Search
          </button>
        </div>

        {/* STATS */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mb-12 md:mb-16">
          {[
            { label: "Live Jobs", count: jobs.filter(j => !isJobExpired(j)).length, icon: <Briefcase size={20} /> },
            { label: "Posted Today", count: jobs.filter(j => new Date(j.posted_on).toDateString() === today.toDateString()).length, icon: <Calendar size={20} /> },
            { label: "Deadline Today", count: jobs.filter(j => new Date(j.application_deadline).toDateString() === today.toDateString()).length, icon: <Clock size={20} /> },
            { label: "Expires Soon", count: jobs.filter(j => {
              const d = Math.ceil((new Date(j.application_deadline) - todayReset) / 86400000);
              return d > 0 && d <= 3;
            }).length, icon: <Clock size={20} /> }
          ].map((stat, i) => (
            <div
              key={i}
              className="bg-green-950/90 border border-green-500/20 rounded-2xl sm:rounded-3xl p-4 sm:p-6
              hover:border-green-400/50 transition-all"
              style={{
                backgroundImage: "linear-gradient(90deg, rgba(7, 66, 29, 0.12) 3px, transparent 1px)",
                backgroundSize: "6px 100%"
              }}
            >
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <div className="p-2 bg-green-500/10 rounded-lg text-white">
                  {stat.icon}
                </div>
                <span className="text-2xl sm:text-3xl md:text-4xl font-black text-white">{stat.count}</span>
              </div>
              <span className="text-white text-[10px] md:text-xs font-black uppercase tracking-wide sm:tracking-widest">
                {stat.label}
              </span>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">

          {/* JOB LIST */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="flex-grow h-px bg-green-500/20" />
              <span className="text-[10px] sm:text-xs font-black uppercase tracking-[0.2em] sm:tracking-[0.3em] text-orange-500 text-center">
                Open Positions ({filteredJobs.filter(j => !isJobExpired(j)).length})
              </span>
              <div className="flex-grow h-px bg-green-500/20" />
            </div>

            {/* Inner scrolling is desktop-only — on mobile the list flows with the page */}
            <div className="space-y-4 lg:max-h-[800px] lg:overflow-y-auto lg:pr-3 custom-scrollbar">
              {loading ? (
                [1, 2, 3].map(i => (
                  <div key={i} className="h-32 rounded-3xl bg-[#04150D] animate-pulse" />
                ))
              ) : filteredJobs.map(job => {
                const jobDeadline = new Date(job.application_deadline);
                const isExpired = isJobExpired(job);
                const isOpen = isDesktop ? selectedJob?.id === job.id : expandedId === job.id;

                return (
                  <div
                    key={job.id}
                    onClick={() => handleCardClick(job)}
                    role="button"
                    tabIndex={0}
                    aria-expanded={isOpen}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleCardClick(job);
                      }
                    }}
                    className={`group relative overflow-hidden p-5 sm:p-8 rounded-3xl sm:rounded-[32px] cursor-pointer transition-all duration-500 border
                      ${isOpen
                        ? "bg-green-950/40 border-green-400 shadow-[0_0_30px_rgba(34,197,94,0.1)]"
                        : "bg-[#04150D] border-green-500/20 hover:border-green-400/50 hover:bg-green-950/20"
                      } ${isExpired ? "opacity-60 grayscale-[0.5]" : ""}`}
                  >
                    {/* Header of Card */}
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-3 mb-2">
                          <h3 className="text-xl md:text-2xl font-black text-orange-500 group-hover:text-green-400 transition-colors">
                            {job.title}
                          </h3>
                          {isExpired && (
                            <span className="bg-red-500/20 text-red-400 border border-red-500/30 text-[10px] font-black px-2 py-0.5 rounded uppercase">
                              Closed
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-3 items-center text-[13px] md:text-sm">
                          <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-500/10 border border-green-500/30 text-white text-[11px] font-black uppercase">
                            <MapPin size={12} className="text-green-400" />
                            {job.location}
                          </span>
                          <span className="flex items-center gap-1.5 text-green-200/60 font-medium">
                            <Calendar size={14} />
                            Posted {new Date(job.posted_on).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      {/* Chevron reads as "tap to open" on touch; arrow keeps the desktop feel */}
                      <ChevronDown className={`lg:hidden shrink-0 ml-3 text-green-300 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                      <ArrowRight className={`hidden lg:block shrink-0 ml-3 transition-transform duration-300 ${isOpen ? 'rotate-90' : 'lg:group-hover:translate-x-2'} text-green-300`} />
                    </div>

                    {/* REVEAL SECTION — hover on desktop, tap-to-expand on touch screens */}
                    <div className={`grid transition-all duration-500 ease-in-out
                      ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr] lg:group-hover:grid-rows-[1fr]'}`}>
                      <div className="overflow-hidden">
                        <div className="pt-5 mt-5 sm:pt-6 sm:mt-6 border-t border-green-500/10 space-y-5 sm:space-y-6">
                          {/* Description */}
                          <div>
                            <div className="flex items-center gap-2 text-green-400 text-[11px] font-black uppercase tracking-widest mb-2">
                              <FileText size={14} /> Description
                            </div>
                            <p className={`text-green-100/70 text-sm leading-relaxed transition-all
                              ${isOpen ? '' : 'line-clamp-3 lg:group-hover:line-clamp-none'}`}>
                              {job.description}
                            </p>
                          </div>

                          {/* Requirements */}
                          <div>
                            <div className="flex items-center gap-2 text-green-400 text-[11px] font-black uppercase tracking-widest mb-2">
                              <ListChecks size={14} /> Key Requirements
                            </div>
                            <p className="text-green-100/70 text-sm leading-relaxed whitespace-pre-line">
                              {job.requirements}
                            </p>
                          </div>

                          {/* Deadline */}
                          <div className={`flex items-center gap-2 text-[11px] font-bold italic ${isExpired ? "text-red-400" : "text-orange-400"}`}>
                            <Clock size={14} />
                            {isExpired ? "Deadline Passed: " : "Deadline: "}
                            {jobDeadline.toDateString()}
                          </div>

                          {/* Mobile only — desktop applies through the side panel */}
                          <div className="lg:hidden pt-1">
                            {isExpired ? (
                              <div className="w-full py-4 rounded-2xl text-center bg-red-500/10 border border-red-500/20 text-red-300/70 text-xs font-black uppercase tracking-widest">
                                Applications Closed
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setSheetJob(job); }}
                                className="w-full py-4 rounded-2xl bg-green-500 text-[#02130C] text-xs font-black uppercase tracking-widest
                                  shadow-[0_0_25px_rgba(34,197,94,0.25)] active:scale-[0.98] transition-transform"
                              >
                                Apply Now
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* APPLY FORM COLUMN — desktop only; mobile applies through the sheet below */}
          {isDesktop && (
          <div ref={applyPanelRef} className="hidden lg:block lg:sticky lg:top-32 scroll-mt-20">
            {selectedJob ? (
              new Date(selectedJob.application_deadline) >= todayReset ? (
                <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <ApplyJob jobId={selectedJob.id} jobTitle={selectedJob.title} />
                </div>
              ) : (
                <div className="min-h-[280px] md:h-[400px] flex flex-col items-center justify-center
                  bg-red-950/10 border-2 border-dashed border-red-500/20
                  rounded-[32px] md:rounded-[48px] text-red-300/60 text-center p-8 md:p-12">
                  <AlertCircle className="text-red-500 mb-4 w-9 h-9 md:w-12 md:h-12" />
                  <h4 className="text-white text-lg md:text-xl font-bold uppercase tracking-tight">Applications Closed</h4>
                  <p className="max-w-xs mt-2 text-sm leading-relaxed">
                    The deadline for the <b>{selectedJob.title}</b> position has passed. We are no longer accepting new applications for this role.
                  </p>
                </div>
              )
            ) : (
              <div className="min-h-[320px] md:h-[600px] flex flex-col items-center justify-center
                bg-[#04150D]/50 border-2 border-dashed border-green-500/20
                rounded-[32px] md:rounded-[48px] text-green-300/60 text-center p-8 md:p-12">
                <Briefcase className="text-green-500/40 w-8 h-8 md:w-9 md:h-9" />
                <h4 className="text-white text-lg md:text-xl font-bold mt-5 md:mt-6">Ready to apply?</h4>
                <p className="max-w-xs mt-2 text-sm md:text-base font-medium">
                  Select a position from the list to view requirements and open the application form.
                </p>
              </div>
            )}
          </div>
          )}

        </div>
      </div>

      {/* MOBILE APPLY SHEET — portalled to <body>, because `.tech-bg > *` puts
          this page inside a z-index:1 stacking context that sits under the navbar */}
      {sheetJob && !isDesktop && createPortal(
        <div
          className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm lg:hidden sheet-overlay"
          onClick={() => setSheetJob(null)}
        >
          <div
            className="absolute inset-x-0 bottom-0 top-8 flex flex-col
              bg-[#04150D] border-t border-green-500/25 rounded-t-[28px] overflow-hidden sheet-panel"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-green-500/15 shrink-0">
              <div className="min-w-0">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-green-500">Applying for</p>
                <h4 className="text-base font-bold text-orange-500 truncate">{sheetJob.title}</h4>
              </div>
              <button
                type="button"
                onClick={() => setSheetJob(null)}
                aria-label="Close application form"
                className="shrink-0 w-10 h-10 -mr-1 flex items-center justify-center rounded-full bg-white/5 text-white/70 active:bg-white/10"
              >
                <X size={20} />
              </button>
            </div>

            <div
              className="flex-1 overflow-y-auto overscroll-contain px-4 pt-5 custom-scrollbar"
              style={{ paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}
            >
              <ApplyJob
                jobId={sheetJob.id}
                jobTitle={sheetJob.title}
                embedded
                onClose={() => setSheetJob(null)}
              />
            </div>
          </div>
        </div>,
        document.body
      )}

      <section id="Footer">
          <Footer />
      </section>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(34, 197, 94, 0.05);
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(34, 197, 94, 0.2);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(34, 197, 94, 0.4);
        }

        /* Mobile apply sheet */
        @keyframes sheetFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes sheetSlideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .sheet-overlay {
          animation: sheetFadeIn 0.2s ease-out;
        }
        .sheet-panel {
          animation: sheetSlideUp 0.3s cubic-bezier(0.32, 0.72, 0, 1);
        }
        @media (prefers-reduced-motion: reduce) {
          .sheet-overlay, .sheet-panel { animation: none; }
        }
      `}</style>
    </div>
  );
};

export default Careers;